import { Monitor, Search, AlertCircle, FileQuestion } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"

interface EmptyStateProps {
  onAction?: () => void
}

export function NoDevicesEmpty({ onAction }: EmptyStateProps) {
  return (
    <Card className="bg-card border-border" data-tour="devices-empty">
      <CardContent className="flex flex-col items-center justify-center py-16">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted/50 mb-4">
          <Monitor className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No devices registered yet</h3>
        <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
          Add your first device to get started monitoring and managing your network.
        </p>
        <Button onClick={onAction} className="bg-primary hover:bg-primary/90 text-primary-foreground">
          Add Device
        </Button>
      </CardContent>
    </Card>
  )
}

export function NoSearchResultsEmpty({ onAction }: EmptyStateProps) {
  return (
    <Card className="bg-card border-border" data-tour="devices-no-results">
      <CardContent className="flex flex-col items-center justify-center py-16">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted/50 mb-4">
          <Search className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No devices match your search</h3>
        <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
          Try adjusting your filters or search term to find what you&apos;re looking for.
        </p>
        <Button variant="link" onClick={onAction} className="text-primary hover:text-primary/80">
          Clear filters
        </Button>
      </CardContent>
    </Card>
  )
}

interface DeviceNotFoundStateProps {
  onBack?: () => void
  actionLabel?: string
}

export function DeviceNotFoundState({
  onBack,
  actionLabel = "Back to Devices",
}: DeviceNotFoundStateProps) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="flex flex-col items-center justify-center py-16">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted/50 mb-4">
          <FileQuestion className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">Device not found</h3>
        <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
          This device does not exist or may have been removed.
        </p>
        {onBack && (
          <Button onClick={onBack} className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {actionLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

interface ClientNotFoundStateProps {
  onAction?: () => void
  actionLabel?: string
  description?: string
}

export function ClientNotFoundState({
  onAction,
  actionLabel = "Back to Clients",
  description = "This client does not exist or may have been removed.",
}: ClientNotFoundStateProps) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="flex flex-col items-center justify-center py-16">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted/50 mb-4">
          <FileQuestion className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">Client not found</h3>
        <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
          {description}
        </p>
        {onAction && (
          <Button onClick={onAction} className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {actionLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

export function ApiErrorState({ onRetry, resourceName = "devices" }: { onRetry?: () => void; resourceName?: string }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="flex flex-col items-center justify-center py-16">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 mb-4">
          <AlertCircle className="h-8 w-8 text-destructive" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">Something went wrong</h3>
        <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
          We couldn&apos;t load the {resourceName}. Please try again.
        </p>
        <div className="flex items-center gap-4">
          <Button onClick={onRetry} className="bg-primary hover:bg-primary/90 text-primary-foreground">
            Retry
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
